import React, { useRef, useEffect } from "react";
import { ReactComponent as FolderIcon } from "assets/images/v2/folder-notification-icon.svg";
import { ReactComponent as PaperIcon } from "assets/images/v2/paper-notification-icon.svg";
import { NOTIFICATIONS_TITLE } from "utilities/Constant";
import { formatNotificationDate } from "utilities/string";
import { NotificationType } from "pages/authentication/userService";
import "./notifications.scss";

export interface NotificationProps {
  readonly id: string;
  readonly icon: "folder" | "paper";
  readonly title: string;
  readonly text: string;
  readonly createdOn: Date;
  readonly notificationType?: NotificationType;
  readonly palInstanceId?: string;
  readonly onRemoveViewedNotification?: (id: string, notificationType?: NotificationType) => void;
  readonly onNotificationClick?: () => void;
}

export interface NotificationsProps {
  readonly notifications?: NotificationProps[];
  readonly onNotificationClick?: (id: string) => void;
  readonly onDocumentNotificationClick?: (documentId: string) => void;
  readonly isOpen?: boolean;
  readonly onClose?: () => void;
  readonly triggerElementSelector?: string;
  readonly autoCloseOnClick?: boolean;
}

const Notification: React.FC<NotificationProps & { onDocumentNotificationClick?: (documentId: string) => void }> = ({
  id,
  icon,
  title,
  text,
  createdOn,
  notificationType,
  palInstanceId,
  onRemoveViewedNotification,
  onNotificationClick,
  onDocumentNotificationClick,
}: NotificationProps & { onDocumentNotificationClick?: (documentId: string) => void }) => {
  const handleClick = () => {
    onRemoveViewedNotification?.(id, notificationType);
    
    // Call the custom notification click handler if provided
    if (onNotificationClick) {
      onNotificationClick();
    } else {
      // Fallback to document notification click for backwards compatibility
      onDocumentNotificationClick?.(palInstanceId || id);
    }
  };

  return (
    <div className="notification__container">
      <button className={`notification__icon ${icon}`} onClick={handleClick}>
        {icon === "folder" && <FolderIcon />}
        {icon === "paper" && <PaperIcon />}
      </button>
      <button className="notification__content" onClick={handleClick}>
        <div className="notification__title label-large-medium">{title}</div>
        <div className="notification__text paragraph-small-regular">{text}</div>
        <div className="notification__date label-small-regular">{formatNotificationDate(createdOn)}</div>
      </button>
    </div>
  );
};

export default function Notifications({
  notifications,
  onNotificationClick,
  onDocumentNotificationClick,
  isOpen = true,
  onClose,
  triggerElementSelector = ".global-header__notification-button",
  autoCloseOnClick = true,
}: NotificationsProps) {
  const notificationsRef = useRef<HTMLDivElement>(null);
  const typeOrder = [NotificationType.Esign, NotificationType.PAL, NotificationType.LoanDetails];
  const getTypeIndex = (notification: NotificationProps) => (notification.notificationType ? typeOrder.indexOf(notification.notificationType) : typeOrder.length);
  notifications?.sort((a, b) => getTypeIndex(a) - getTypeIndex(b));

  const handleRemoveViewedNotification = (id: string, notificationType?: NotificationType) => {
    onNotificationClick?.(id);
    autoCloseOnClick && onClose?.();
  };

  useEffect(() => {
    if (!isOpen || !onClose) return;

    const handleClickOutside = (event: Event) => {
      const target = event.target as Element;
      const triggerElement = document.querySelector(triggerElementSelector);
      if (triggerElement && (triggerElement.contains(target) || triggerElement === target)) {
        return;
      }
      if (notificationsRef.current && !notificationsRef.current.contains(target)) {
        onClose();
      }
    };

    const events = ["mousedown", "touchstart"];
    for (const event of events) {
      document.addEventListener(event, handleClickOutside);
    }

    return () => {
      for (const event of events) {
        document.removeEventListener(event, handleClickOutside);
      }
    };
  }, [isOpen, onClose, triggerElementSelector]);

  if (!isOpen) return null;

  return (
    <div className="notifications__container" ref={notificationsRef}>
      <div className="notifications__header">
        <h6>{NOTIFICATIONS_TITLE}</h6>
      </div>
      <div className="notifications__content">
        {notifications?.map((n) => (
          <Notification
            key={n.id}
            {...n}
            onRemoveViewedNotification={handleRemoveViewedNotification}
            onDocumentNotificationClick={onDocumentNotificationClick}
          />
        ))}
        {(!notifications || notifications.length === 0) && (
          <div className="notifications__empty-text paragraph-small-regular">No new notifications</div>
        )}
      </div>
    </div>
  );
}
